/**
 * Completion content for the "offer-services" activity.
 * Records that approved services were communicated to the member and captures
 * per-service offer details (which info was sent, via what medium).
 * Creates a Communication (progress log) to record the member contact.
 * @agentic connections
 */

import {
  Alert,
  Badge,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type {
  Patient,
  Practitioner,
  PractitionerRole,
  ServiceRequest,
} from "@medplum/fhirtypes";
import { COMMUNICATION_MEDIUM_SELECT_OPTIONS } from "@benchmark/core/constants";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";
import { useCarePlanServiceRequests } from "@/hooks/domain/useCarePlanServiceRequests";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { findBenchmarkPractitionerRole } from "@benchmark/core/services";
import { logActivityProgress } from "@benchmark/core/actions";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import { showWarningNotification } from "@benchmark/ui/utils";
import type { CompletionContentProps } from "./DefaultCompletionContent";
import {
  getCarePlanIdFromTask,
  getLatestApprovalNote,
  getLatestSelectionNote,
  getSelectedServiceDisplay,
  getServiceRequestVisitModeDisplay,
  getServiceTypeDisplay,
  hasSelectedService,
} from "@benchmark/core/resolvers";
import { CONFIG } from "@/app/config";

const OFFER_MEDIUM_OPTIONS = COMMUNICATION_MEDIUM_SELECT_OPTIONS.filter(
  (o) => o.value === "phone" || o.value === "text",
);

/**
 * Read-only card showing approved service details and clinical context for the offer step.
 * @agentic connections
 */
const OfferServiceCard = ({ sr }: { sr: ServiceRequest }) => {
  const serviceTypeDisplay = getServiceTypeDisplay(sr);
  const modeDisplay = getServiceRequestVisitModeDisplay(sr);
  const selectionNote = getLatestSelectionNote(sr);
  const approvalNote = getLatestApprovalNote(sr);

  return (
    <Paper p="sm" withBorder>
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Badge variant="filled" color="purple" size="md" radius="sm">
            {serviceTypeDisplay.toUpperCase()}
          </Badge>
          <Badge variant="light" color="green" size="sm">
            Approved
          </Badge>
        </Group>

        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
            Selected Service:
          </Text>
          <Text size="sm">{getSelectedServiceDisplay(sr)}</Text>
        </Group>

        {modeDisplay !== "—" && (
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
              Service Mode:
            </Text>
            <Text size="sm">{modeDisplay}</Text>
          </Group>
        )}

        {selectionNote?.websiteUrls && selectionNote.websiteUrls.length > 0 && (
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
              {selectionNote.websiteUrls.length === 1
                ? "Website:"
                : "Websites:"}
            </Text>
            <Stack gap={2}>
              {selectionNote.websiteUrls.map((url, i) => (
                <Text key={i} size="sm">
                  {url}
                </Text>
              ))}
            </Stack>
          </Group>
        )}

        {selectionNote?.rationale && (
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
              Selection Rationale:
            </Text>
            <Text size="sm">{selectionNote.rationale}</Text>
          </Group>
        )}

        {approvalNote?.rationale && (
          <Paper p="xs" bg="green.0" radius="sm">
            <Stack gap={2}>
              <Text size="xs" fw={600} c="green.7" tt="uppercase">
                Approval Notes
              </Text>
              <Text size="sm">{approvalNote.rationale}</Text>
              {approvalNote.authorString && (
                <Text size="xs" c="dimmed">
                  — {approvalNote.authorString}
                </Text>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>
    </Paper>
  );
};

/**
 * Form state for the offer-services completion wizard.
 * @agentic connections
 */
type OfferServicesFormData = {
  contactMethod: string;
  notes: string;
};

/**
 * Completion form for offer-services: review approved services, select contact medium, provide notes.
 * @agentic connections
 */
export const OfferServicesCompletionContent = ({
  task,
  onComplete,
  onCancel,
  onDirtyChange,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextActivityAppliedRule,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  isResolvingNextTaskOwner = false,
}: CompletionContentProps) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const patientId = getResourceIdFromReference(task.for?.reference, "Patient");
  const { data: patientResourceRaw } = usePatientResource(patientId);
  const patient = patientResourceRaw as Patient | undefined;
  const [practitionerRole, setPractitionerRole] = useState<
    PractitionerRole | undefined
  >();

  useEffect(() => {
    if (!profile?.id) return;
    findBenchmarkPractitionerRole(medplum, {
      reference: `Practitioner/${profile.id}`,
      benchmarkOrganizationId: CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    })
      .then(setPractitionerRole)
      .catch(() => setPractitionerRole(undefined));
  }, [medplum, profile?.id]);

  const form = useForm<OfferServicesFormData>({
    initialValues: {
      contactMethod: "",
      notes: "",
    },
    validate: {
      contactMethod: (value) =>
        !value ? "Select how the services were communicated" : null,
      notes: (value) =>
        !value.trim()
          ? "Provide details about what was communicated to the member"
          : null,
    },
  });

  const carePlanId = getCarePlanIdFromTask(task);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();

  const {
    serviceRequests,
    isLoading,
    error: srError,
  } = useCarePlanServiceRequests(carePlanId);

  const selectedServiceRequests = useMemo(
    () => serviceRequests.filter(hasSelectedService),
    [serviceRequests],
  );

  const steps = useMemo(
    () => [
      {
        id: "details",
        title: "Details",
        subtitle: "Review services and record how they were offered",
        requiredFields: ["contactMethod", "notes"],
        isValid: (values: OfferServicesFormData) =>
          !!values.contactMethod && values.notes.trim().length > 0,
      },
      {
        id: "assignment",
        title: "Assignment",
        subtitle: "Review next task assignment",
      },
    ],
    [],
  );

  useEffect(() => {
    onDirtyChange?.(
      !!form.values.contactMethod ||
        form.values.notes.trim().length > 0 ||
        !!nextTaskOwnerOverride ||
        !!careMinutes,
    );
  }, [form.values, nextTaskOwnerOverride, careMinutes, onDirtyChange]);

  const handleSubmit = useCallback(
    async (values: OfferServicesFormData) => {
      if (!values.contactMethod || !values.notes.trim()) return;

      setSubmitting(true);
      setError(null);

      try {
        const methodLabel =
          OFFER_MEDIUM_OPTIONS.find((o) => o.value === values.contactMethod)
            ?.label || values.contactMethod;
        const offeredServicesSummary = selectedServiceRequests
          .map(
            (sr) =>
              `${getServiceTypeDisplay(sr)}: ${getSelectedServiceDisplay(sr)}`,
          )
          .join("; ");

        if (task.id && patient && profile && practitionerRole) {
          try {
            await logActivityProgress(medplum, {
              task,
              progressData: {
                contactMethod: values.contactMethod as "phone" | "text",
                outcome: "general-progress",
                outcomeDisplay: "Services Offered",
                notes: `${offeredServicesSummary ? `Services: ${offeredServicesSummary}. ` : ""}${values.notes}`,
                snoozeBehavior: { type: "none" },
              },
              patient,
              practitioner: profile,
              practitionerRole,
            });
          } catch {
            showWarningNotification(
              "Offer interaction could not be logged, but completion will proceed.",
            );
          }
        }

        const statusReason = [
          `Sent via ${methodLabel}`,
          offeredServicesSummary && `Services: ${offeredServicesSummary}`,
          `Notes: ${values.notes}`,
        ]
          .filter(Boolean)
          .join(". ");

        await onComplete(statusReason);
        await teeForTask({
          task,
          minutes: careMinutes,
          method: "task-completion",
          notes: values.notes || undefined,
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to complete task",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      selectedServiceRequests,
      onComplete,
      task,
      patient,
      profile,
      practitionerRole,
      medplum,
      teeForTask,
      careMinutes,
    ],
  );

  if (!carePlanId) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Unable to determine care plan from this task.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Loading selected services...
        </Text>
      </Stack>
    );
  }

  if (srError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Failed to load service requests: {srError}
      </Alert>
    );
  }

  if (selectedServiceRequests.length === 0) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        color="orange"
        variant="light"
      >
        No selected services found. Complete Select Services before offering
        services to the member.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={handleSubmit}
        isSubmitting={submitting}
        submitText="Complete Offer Services"
        onCancel={onCancel}
      >
        <FormStep id="details">
          <Stack gap="md">
            <Text size="sm">
              Review the approved services below. Note which details (e.g.
              specific website, practitioner) you communicated to the member.
            </Text>

            {selectedServiceRequests.map((sr) => (
              <OfferServiceCard key={sr.id} sr={sr} />
            ))}

            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Contact Method{" "}
                <Text span c="red">
                  *
                </Text>
              </Text>
              <CardSelect
                data={OFFER_MEDIUM_OPTIONS}
                value={form.values.contactMethod || null}
                onChange={(value) => {
                  const singleValue = Array.isArray(value) ? value[0] : value;
                  form.setFieldValue("contactMethod", singleValue ?? "");
                }}
                columns={2}
                compact
                error={!!form.errors.contactMethod}
                aria-label="Select contact method"
              />
            </Stack>

            <Textarea
              label="Offer Details"
              placeholder="What was communicated? E.g. which website link was sent, which practitioner was mentioned..."
              required
              {...form.getInputProps("notes")}
              autosize
              minRows={2}
              maxRows={4}
            />
          </Stack>
        </FormStep>

        <FormStep id="assignment">
          <Stack gap="md">
            <NextTaskOwnerSection
              nextActivityLabel={nextActivityLabel}
              defaultOwner={nextActivityDefaultOwner}
              appliedRule={nextActivityAppliedRule}
              overrideOwner={nextTaskOwnerOverride}
              onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
              isResolving={isResolvingNextTaskOwner}
            />
            <CareTimeActivityDurationField
              minutes={careMinutes}
              onMinutesChange={setCareMinutes}
            />
          </Stack>
        </FormStep>
      </SteppedForm>
    </Stack>
  );
};
